import { SeatsAvailabilityApi, SeatsRequest } from '@api/seats'
import date from '@lib/date'
import passengersUtils from '@lib/passengers'
import utils from '@lib/utils'
import { PassengerData } from '@stores/checkout'
import { ParamsStore } from '@stores/params'

const getSeatsCount = (seats?: Seat.BySegment | null): number => {
  if (!seats) return 0

  return Object.values(seats).reduce((acc, currentValue) => acc + currentValue.length, 0)
}

export interface BuildSeatsProps {
  limitations?: boolean
  fareClass?: string | null
  passengers?: PassengerData[]
  cards?: DiscountCode.Card[]
}

const buildSeatAvailabilitiesParams = (
  connection: Connection,
  params: ParamsStore,
  props?: BuildSeatsProps,
): SeatsAvailabilityApi.Request => {
  const { currency } = params

  let passengers = null

  if (props?.passengers?.length) {
    passengers = props.passengers
  }

  let normalizedPassengers = null

  if (passengers !== null) {
    normalizedPassengers = passengersUtils
      .normalizePassengers(passengers)
      .map(pax => ({ ...pax, cards: passengersUtils.getPaxCards(pax.cards) }))
  }

  return utils.object.compact<SeatsAvailabilityApi.Request>({
    retailerPartnerNumber: params.retailerPartnerNumber,
    marketingCarrierCode: connection.marketingCarrier.code,
    departureStation: connection.departureStation.code,
    arrivalStation: connection.arrivalStation.code,
    departureTime: date.formatDateISO(date.parse(connection.departureTime, 'UTC')),
    arrivalTime: date.formatDateISO(date.parse(connection.arrivalTime, 'UTC')),
    fareClass: props?.fareClass,
    offerId: connection.offerId ?? null,
    currency,
    passengers: normalizedPassengers,
    cards: props?.cards?.length ? props.cards : null,
  })
}

const buildSeatsParams = (connection: Connection, params: ParamsStore, props?: BuildSeatsProps): SeatsRequest => {
  const baseParams = buildSeatAvailabilitiesParams(connection, params, props)

  return utils.object.compact<SeatsRequest>({
    ...baseParams,
    extraFields: props?.limitations
      ? /* istanbul ignore next: fallback to be covered with unit tests */
        'cars.limitations,cars.seats.limitations'
      : null,
    locale: params.locale,
  })
}

const toUrlParams = (selected: Seat.BySegment): Record<string, Seat.UrlParam[]> => {
  const entries = Object.entries(selected).map(([key, value]) => [
    key,
    value.map(item => ({
      price: item.price ?? 0,
      code: item.code,
      label: item.label,
      fareClass: item.fareClass,
      segmentIndex: item.segmentIndex,
    })),
  ])

  return Object.fromEntries(entries)
}

const sumPrice = (seats: Pick<Seat.Selected, 'price'>[]): number => utils.array.sum(seats, seat => Number(seat.price))

const sumOutboundPrice = (selected: Seat.BySegment, connection: Connection | null, currency: Currency): Money =>
  Object.values(selected)
    .flatMap(item => item.map(el => ({ fareClass: el.fareClass, price: el.price })))
    .reduce(
      (acc, curr): Money => {
        const current = connection?.fares.find(el => el.fareClass.code === curr.fareClass)

        return {
          fractional:
            acc.fractional +
            (current?.price.fractional ?? /* istanbul ignore next: fallback to be covered with unit tests */ 0) +
            Number(curr.price),
          currency,
        }
      },
      { fractional: 0, currency },
    )

/* istanbul ignore next: should be covered separately by unit tests */
const getSeatsLimitations = (car: Seat.Car): DiscountCard.Item[] =>
  car.seats.flatMap(seat => seat.limitations ?? /* istanbul ignore next: fallback to be covered with unit tests */ [])

/* istanbul ignore next: should be covered separately by unit tests */
const getDiscountCategories = (cars: Seat.Car[]): DiscountCard.Item[] => {
  const limitations = cars.flatMap(car => [...getSeatsLimitations(car), ...(car.limitations ?? [])])

  return utils.array.uniqueBy(limitations, 'name')
}

const flatten = (seats?: Seat.BySegment): Seat.Selected[] => Object.values(seats ?? {}).flat()

/* istanbul ignore next: should be covered separately by unit tests */
const isLimitedBy = (seat: Seat.Entry, discount: string): boolean =>
  !!utils.array.findBy(seat.limitations ?? [], 'name', discount)

/* istanbul ignore next: should be covered separately by unit tests */
const isLimitedSeatsTaken = (seats: Seat.Entry[], discountCard: string): boolean => {
  let limitedSeatFound = false

  for (const seat of seats) {
    if (isLimitedBy(seat, discountCard)) {
      if (seat.vacant) return false

      limitedSeatFound = true
    }
  }

  return limitedSeatFound
}

/**
 * Why do we need to normalize seat orientation?
 *
 * The default seat orientation in the internal system is from left to right, represented by 'facingForward'.
 * Older APIs use 'facing_forward' for seats rotated in the opposite direction (right to left).
 * This function maps external orientation keys to internal ones while preserving the intended rotation.
 *
 * @param {Seat.Entry} seat - The seat object to normalize.
 * @returns {Seat.Entry} - The normalized seat object with updated orientation.
 */
const normalizeSeat = (seat: Seat.Entry): Seat.Entry => {
  const swappedMap: Record<string, Seat.SeatOrientation> = {
    facing_forward: 'facingBackward',
    facing_backward: 'facingForward',
    facing_right: 'facingLeft',
    facing_left: 'facingRight',
  }

  return {
    ...seat,
    orientation: swappedMap[seat.orientation ?? 'facing_backward'],
  }
}

const transformSegmentIntoTrainSeatsScheme =
  (tripDirection: Seat.TripDirection) =>
  ({ segment, cars }: Seat.Data) => ({
    id: segment.id,
    index: segment.index,
    departureTime: segment.departureTime,
    arrivalTime: segment.arrivalTime,
    departureStation: segment.departureStation,
    arrivalStation: segment.arrivalStation,
    cars: cars.map<Seat.Train.Car>(car => ({
      label: car.label ?? String(car.index + 1),
      index: car.index,
      layout: `${tripDirection}.${segment.index}.${car.index}`,
    })),
  })

const transformIntoTrainSeats = (seats: Seat.ByTripDirection<Seat.Data[]>): Seat.Train.Data => {
  const scheme = {
    outbound: seats.outbound.map<Seat.Train.Segment>(transformSegmentIntoTrainSeatsScheme('outbound')),
    inbound: seats.inbound.map<Seat.Train.Segment>(transformSegmentIntoTrainSeatsScheme('inbound')),
  }

  return {
    scheme: scheme,
    layoutQuery: layoutKey => {
      const [tripDirection, segmentIndex, carIndex] = layoutKey.split('.')
      const segment = seats[tripDirection as Seat.TripDirection]?.find(
        ({ segment }) => segment.index === Number(segmentIndex),
      )
      const car = segment?.cars.find(({ index }) => index === Number(carIndex))

      const layout = car && {
        code: layoutKey,
        seats: car.seats.map(seat => normalizeSeat(seat)),
      }

      return {
        queryKey: ['seats', 'layout', 'singleStep', layout],
        queryFn: () => layout,
        placeholderData: layout,
      }
    },
  }
}

const seatSelectionUtils = {
  transformIntoTrainSeats,
  getSeatsCount,
  toUrlParams,
  buildSeatsParams,
  buildSeatAvailabilitiesParams,
  sumPrice,
  sumOutboundPrice,
  flatten,
  getDiscountCategories,
  isLimitedSeatsTaken,
}

export default seatSelectionUtils
